import React from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DownloadIcon } from 'lucide-react';
import InsightCard from '../components/Dashboard/InsightCard';
import { incomeStatementData } from '../utils/mockData';
const IncomeStatement = () => {
  // Format currency
  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString()}`;
  };
  // Format percentage
  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };
  // Colors for pie chart
  const COLORS = ['#4F46E5', '#EC4899', '#F97316', '#10B981', '#8B5CF6'];
  return <div data-id="element-1511">
      <div className="mb-6 flex justify-between items-start" data-id="element-1512">
        <div data-id="element-1513">
          <h1 className="text-2xl font-bold text-gray-800" data-id="element-1514">
            Estado de Resultados
          </h1>
          <p className="text-gray-600" data-id="element-1515">
            Análisis detallado de ingresos, costos y utilidades
          </p>
        </div>
        <button className="flex items-center space-x-2 text-sm bg-blue-600 text-white px-4 py-2.5 rounded-md hover:bg-blue-700 transition-colors" data-id="element-1516">
          <DownloadIcon size={18} data-id="element-1517" />
          <span data-id="element-1518">Exportar PDF</span>
        </button>
      </div>
      {/* Summary Table */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm mb-6" data-id="element-1519">
        <h2 className="text-lg font-semibold mb-4" data-id="element-1520">Resumen</h2>
        <div className="overflow-x-auto" data-id="element-1521">
          <table className="w-full" data-id="element-1522">
            <thead data-id="element-1523">
              <tr className="bg-gray-50" data-id="element-1524">
                <th className="py-3 px-4 text-left text-sm font-medium text-gray-500" data-id="element-1525">
                  Concepto
                </th>
                <th className="py-3 px-4 text-right text-sm font-medium text-gray-500" data-id="element-1526">
                  Valor
                </th>
                <th className="py-3 px-4 text-right text-sm font-medium text-gray-500" data-id="element-1527">
                  % de Ingresos
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-1528">
              {incomeStatementData.summary.map((item, index) => {
              const percentage = item.value / incomeStatementData.summary[0].value * 100;
              const isHighlight = [2, 4, 8].includes(index);
              return <tr key={item.name} className={isHighlight ? 'bg-blue-50' : ''} data-id="element-1529">
                    <td className={`py-3 px-4 text-sm ${isHighlight ? 'font-medium' : ''}`} data-id="element-1530">
                      {item.name}
                    </td>
                    <td className={`py-3 px-4 text-right text-sm ${isHighlight ? 'font-medium' : ''}`} data-id="element-1531">
                      {formatCurrency(item.value)}
                    </td>
                    <td className={`py-3 px-4 text-right text-sm ${isHighlight ? 'font-medium' : ''}`} data-id="element-1532">
                      {formatPercentage(percentage)}
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>
      {/* Charts Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1533">
        {/* Ingresos vs Gastos OPEX */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-1534">
          <h2 className="text-lg font-semibold mb-4" data-id="element-1535">
            Ingresos vs Gastos OPEX
          </h2>
          <div className="h-72" data-id="element-1536">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1537">
              <BarChart data={[{
              name: 'Julio 2023',
              ingresos: incomeStatementData.summary[0].value,
              gastos: incomeStatementData.summary[3].value
            }]} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-1538">
                <CartesianGrid strokeDasharray="3 3" data-id="element-1539" />
                <XAxis dataKey="name" data-id="element-1540" />
                <YAxis data-id="element-1541" />
                <Tooltip formatter={value => [`${formatCurrency(value as number)}`, undefined]} data-id="element-1542" />
                <Legend data-id="element-1543" />
                <Bar dataKey="ingresos" name="Ingresos" fill="#4F46E5" data-id="element-1544" />
                <Bar dataKey="gastos" name="Gastos OPEX" fill="#F97316" data-id="element-1545" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Distribución de gastos */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-1546">
          <h2 className="text-lg font-semibold mb-4" data-id="element-1547">Distribución de gastos</h2>
          <div className="h-72" data-id="element-1548">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1549">
              <PieChart data-id="element-1550">
                <Pie data={incomeStatementData.expenses} cx="50%" cy="50%" labelLine={false} label={({
                name,
                percent
              }) => `${name}: ${(percent * 100).toFixed(0)}%`} outerRadius={80} fill="#8884d8" dataKey="value" data-id="element-1551">
                  {incomeStatementData.expenses.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-1552" />)}
                </Pie>
                <Tooltip formatter={value => [formatCurrency(value as number), undefined]} data-id="element-1553" />
                <Legend data-id="element-1554" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* Margins Chart */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-6" data-id="element-1555">
        <h2 className="text-lg font-semibold mb-4" data-id="element-1556">
          Márgenes (Últimos 6 meses)
        </h2>
        <div className="h-72" data-id="element-1557">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1558">
            <LineChart data={incomeStatementData.margins} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-1559">
              <CartesianGrid strokeDasharray="3 3" data-id="element-1560" />
              <XAxis dataKey="month" data-id="element-1561" />
              <YAxis tickFormatter={value => `${value}%`} data-id="element-1562" />
              <Tooltip formatter={value => [`${value}%`, undefined]} data-id="element-1563" />
              <Legend data-id="element-1564" />
              <Line type="monotone" dataKey="bruto" name="Margen bruto" stroke="#4F46E5" strokeWidth={2} data-id="element-1565" />
              <Line type="monotone" dataKey="neto" name="Margen neto" stroke="#F97316" strokeWidth={2} data-id="element-1566" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Insights Section */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-1567">
        <h2 className="text-lg font-semibold mb-4" data-id="element-1568">Insights</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1569">
          <InsightCard type="warning" message="El gasto en marketing subió 34% vs mes pasado, representando un 18% de tus gastos operativos." data-id="element-1570" />
          <InsightCard type="negative" message="Tu margen bruto cayó 1.9 puntos porcentuales debido al aumento en costo de compra de materiales." data-id="element-1571" />
          <InsightCard type="info" message="Tus gastos fijos representan el 75% del total de gastos operativos." data-id="element-1572" />
          <InsightCard type="positive" message="A pesar del aumento en costos, tu utilidad neta sigue siendo positiva y representa el 14.8% de los ingresos." data-id="element-1573" />
        </div>
      </div>
    </div>;
};
export default IncomeStatement;